import React from 'react';
import { GlobalSettingsInterface, LinkInterface } from '@/types';
import { bgColorClassname } from '@/utils';
import { Heading, FeaturedProgram, CustomProgram, RichText } from '@/components';
import type { ImageAsset, PortableTextBlock as PortableTextBlockType } from '@sanity/types';
import Cookies from 'universal-cookie';

// Define interfaces for the data structures
interface Frequency {
  startDate: string;
  endDate: string;
  rrule: string;
}

interface ImageWithAlt {
  alt?: string;
  image: {
    asset: ImageAsset;
  };
}

interface ImageWithMask extends ImageWithAlt {
  mask: string;
}

interface Slug {
  current?: string;
}

interface PageParent {
  slug: Slug;
  pageParent?: PageParent;
}

interface FeaturedEntry {
  _id: string;
  _type: string;
  slug?: Slug;
  pageParent?: PageParent;
  title: string;
  description?: string;
  featuredImage?: ImageWithAlt;
  location?: string;
  startDate?: string;
  endDate?: string;
  frequency?: Frequency;
  image?: ImageWithMask;
  subtitle?: string;
  footer?: PortableTextBlockType;
  blurb?: PortableTextBlockType;
  includeCta?: boolean;
  cta?: LinkInterface;
  price?: number;
  duration?: string;
  recommendedAges?: string;
  language: string;
  seo: {
    description?: string;
    image?: ImageWithAlt;
  };
}

interface EntryPromoProps {
  backgroundColor?: {
    label: string;
    value: string;
  };
  layoutWidth?: string;
  globalSettings: GlobalSettingsInterface[];
  title: string;
  includeCta: boolean;
  cta?: LinkInterface;
  blurb?: PortableTextBlockType;
  imageLayout?: string;
  featuredEntries: FeaturedEntry[];
  isBiggerLayout?: boolean;
}

// Functional component to display promo entries
const EntryPromo: React.FC<EntryPromoProps> = ({
  backgroundColor,
  layoutWidth,
  globalSettings,
  title,
  includeCta,
  cta,
  blurb,
  imageLayout,
  featuredEntries,
  isBiggerLayout,
}) => {
  // Define mask types and get the appropriate type based on index
  const maskTypes = ['circle', 'blob', 'square', 'shield'];
  const getMaskType = (index: number) => maskTypes[index % maskTypes.length];

  const getGridClasses = (entriesLength: number, layoutWidth: string | undefined): string => {
    switch (entriesLength) {
      case 4:
        return 'sm:grid-cols-2 lg:grid-cols-4 gap-8';
      case 3:
        return layoutWidth === '2/3'
          ? 'sm:grid-cols-2 md:grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-8 xl:gap-10'
          : layoutWidth === '1/3'
            ? 'grid-cols-1 gap-8 xl:gap-10'
            : layoutWidth
              ? 'sm:grid-cols-2 md:grid-cols-1 lg:grid-cols-3 gap-8 xl:gap-10'
              : 'sm:grid-cols-2 md:grid-cols-3 gap-8 xl:gap-10';
      case 2:
        return 'sm:grid-cols-2 md:grid-cols-1 lg:grid-cols-2 gap-8 xl:gap-14';
      case 1:
        return 'grid-cols-1 gap-8 lg:gap-8 xl:gap-14';
      default:
        return 'grid-cols-1 sm:grid-cols-2 md:grid-cols-2 lg:grid-cols-3 gap-8 md:gap-10 xl:gap-12';
    }
  };

  const gridClasses = getGridClasses(featuredEntries.length, layoutWidth);

  // Determine container classes based on imageLayout
  const containerClasses =
    imageLayout === 'imageBesideContent' ? 'flex flex-col gap-7' : `grid ${gridClasses}`;

  const cookies = new Cookies();
  const preferredLanguage = cookies.get('preferredLanguage') || 'en';

  const filteredData = globalSettings.find((edge) => edge.node.language === preferredLanguage);

  const metadata = filteredData ? filteredData?.node?.seo : undefined;

  return (
    <div className={backgroundColor ? bgColorClassname(backgroundColor) : ''}>
      <div className='mx-auto flex max-w-screen-xl flex-col gap-5 px-5 py-8 md:p-[50px]'>
        <Heading
          title={title}
          className='mb-0'
          isShowButton={includeCta}
          cta={cta}
        />
        {blurb && (
          <div className='my-3 text-xl font-normal'>
            <RichText
              as='p'
              value={blurb}
            />
          </div>
        )}
        {featuredEntries.length > 0 && (
          <div className={`${containerClasses}`}>
            {featuredEntries.map((entry, index) => (
              <React.Fragment key={index}>
                {entry._type === 'customPromo' ? (
                  <CustomProgram
                    className='w-full'
                    id={entry._id}
                    entry={entry}
                    imageContainerClass='px-8 sm:px-0'
                    imageLayout={imageLayout}
                  />
                ) : (
                  <FeaturedProgram
                    className='w-full'
                    id={entry._id}
                    entry={entry}
                    titleClassName='text-xl sm:text-2xl font-semibold mb-1.5'
                    imageRounded
                    showTimeLocation
                    imageContainerClass='px-8 sm:px-0'
                    maskType={getMaskType(index)}
                    imageLayout={imageLayout}
                    isBiggerLayout={isBiggerLayout}
                    defaultSEO={metadata}
                  />
                )}
              </React.Fragment>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default EntryPromo;
